/** Email-safe HTML for standalone button blocks (table alignment, not flexbox). */

import { escapeHtml } from './helpers';

export type ButtonBlockHtmlOptions = {
  isMobile?: boolean;
  fontFamily?: string;
};

function computeButtonContainerPadding(styles: Record<string, unknown>, isMobile: boolean): string {
  if (styles.paddingTop || styles.paddingRight || styles.paddingBottom || styles.paddingLeft) {
    return `${styles.paddingTop || '0px'} ${styles.paddingRight || '0px'} ${styles.paddingBottom || '0px'} ${styles.paddingLeft || '0px'}`;
  }
  if (styles.padding) return String(styles.padding);
  return isMobile ? '16px 20px' : '20px';
}

function resolveTableAlign(textAlign: string): 'left' | 'center' | 'right' {
  if (textAlign === 'left' || textAlign === 'right') return textAlign;
  return 'center';
}

export function renderButtonBlockHtml(
  content: Record<string, unknown>,
  styles: Record<string, unknown>,
  options: ButtonBlockHtmlOptions = {}
): string {
  const isMobile = options.isMobile === true;
  const buttonStyle = (styles.buttonStyle as Record<string, unknown>) || {};
  const variant = (buttonStyle.variant as string) || 'solid';
  const backgroundColor = (buttonStyle.backgroundColor as string) || '#3B82F6';
  const textColor = (buttonStyle.color as string) || '#FFFFFF';
  const borderRadius = (buttonStyle.borderRadius as string) || '6px';
  const buttonWidth = (buttonStyle.width as string) || 'auto';
  const borderColor = (buttonStyle.borderColor as string) || textColor || backgroundColor;
  const paddingValue =
    (buttonStyle.padding as string) || (isMobile ? '10px 20px' : '12px 24px');
  const fontSize = (buttonStyle.fontSize as string) || (isMobile ? '14px' : '16px');
  const fontWeight = (buttonStyle.fontWeight as string) || '600';
  const fontFamily = buttonStyle.fontFamily
    ? `'${String(buttonStyle.fontFamily).replace(/'/g, '')}', sans-serif`
    : options.fontFamily || 'Arial, sans-serif';
  const textTransform = (buttonStyle.textTransform as string) || 'none';
  const letterSpacing = (buttonStyle.letterSpacing as string) || '0px';
  const gradient = buttonStyle.gradient as string | undefined;

  const resolveBackground = (): string => {
    if (variant === 'gradient') {
      return gradient || `linear-gradient(135deg, ${backgroundColor}, #7C3AED)`;
    }
    if (variant === 'outline' || variant === 'ghost') return 'transparent';
    return backgroundColor;
  };

  const resolveBorder = (): string => {
    if (variant === 'outline') return `2px solid ${borderColor}`;
    if (variant === 'ghost') return '1px solid transparent';
    return '1px solid transparent';
  };

  const resolveTextColor = (): string => {
    if (variant === 'outline' || variant === 'ghost') return textColor || backgroundColor;
    return textColor;
  };

  const textAlign = (styles.textAlign as string) || 'center';
  const tableAlign = resolveTableAlign(textAlign);
  const containerPadding = computeButtonContainerPadding(styles, isMobile);
  const containerBg = (styles.backgroundColor as string) || 'transparent';

  const isFullWidth =
    buttonWidth !== 'auto' && (buttonWidth.includes('%') || buttonWidth.includes('px'));
  const anchorDisplay = isFullWidth ? 'block' : 'inline-block';
  const widthStyle = isFullWidth ? `width: ${buttonWidth}; max-width: 100%;` : '';

  const actionType = (content.actionType as 'open-url' | 'download-file') || 'open-url';
  const isDownloadAction = actionType === 'download-file';
  const buttonHref = isDownloadAction
    ? String(content.downloadFileUrl || '').trim() || '#'
    : String(content.url || '#').trim() || '#';
  const buttonTarget = isDownloadAction ? '_self' : (content.target as string) || '_blank';
  const relAttr = buttonTarget === '_blank' ? ' rel="noopener noreferrer"' : '';
  const downloadName = (content.downloadAs as string) || (content.downloadFileName as string) || '';
  const downloadAttr = isDownloadAction
    ? downloadName
      ? ` download="${escapeHtml(downloadName)}"`
      : ' download'
    : '';

  const bg = resolveBackground();
  const useGradient = variant === 'gradient' && bg.includes('gradient');
  const anchorBg = useGradient ? backgroundColor : bg;
  const anchorStyle = [
    `display: ${anchorDisplay}`,
    `background-color: ${anchorBg}`,
    useGradient ? `background: ${bg}` : '',
    `color: ${resolveTextColor()}`,
    `padding: ${paddingValue}`,
    `border-radius: ${borderRadius}`,
    `font-size: ${fontSize}`,
    `font-weight: ${fontWeight}`,
    `font-family: ${fontFamily}`,
    `text-decoration: none`,
    `text-align: center`,
    `border: ${resolveBorder()}`,
    `text-transform: ${textTransform}`,
    `letter-spacing: ${letterSpacing}`,
    'box-sizing: border-box',
    'mso-padding-alt: 0',
    widthStyle,
  ]
    .filter(Boolean)
    .join('; ');

  const buttonLabel = escapeHtml(String(content.text || 'Button'));

  return `
    <table role="presentation" class="button-block-wrap" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; border-collapse: collapse;">
      <tr>
        <td align="${tableAlign}" style="padding: ${containerPadding}; text-align: ${textAlign}; background-color: ${containerBg};">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${tableAlign}" style="margin: ${tableAlign === 'center' ? '0 auto' : tableAlign === 'right' ? '0 0 0 auto' : '0'};">
            <tr>
              <td align="${tableAlign}" style="border-radius: ${borderRadius}; text-align: ${textAlign};">
                <a href="${escapeHtml(buttonHref)}" target="${escapeHtml(buttonTarget)}"${relAttr}${downloadAttr} style="${anchorStyle}">${buttonLabel}</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `.trim();
}
